import { type PackagingInformationEntry } from "../../../models/ProductData";
import {
  selectInProductionFlatList,
  selectPackageInStockAmount,
  selectInStockFlatList,
} from "../../pna/packagingInformationEntrySelectors";
import dayjs from "dayjs";
import sum from "lodash/sum";
import { groupByFn } from "../../reducers";
import { type StockData, type StockEntry } from "~/utils/cartUpdating/delivery/stockDataTypes";

const TYPE_ORDER = {
  stock: 1,
  production: 2,
  replenishment: 3,
} as const;

export function getStockData(packageData: PackagingInformationEntry): StockData {
  const inStock = selectPackageInStockAmount(packageData);
  const inProduction = selectInProductionFlatList(packageData);
  const inStockData = selectInStockFlatList(packageData);

  const hasInStock = inStock > 0;
  const hasInProduction = inProduction.length > 0;

  const rawStock: StockData = [];
  if (hasInStock) {
    inStockData.forEach((entry) => {
      rawStock.push({
        amount: entry.quantity,
        date: entry.date,
        type: "stock",
      });
    });
  }
  if (hasInProduction) {
    inProduction.forEach((entry) => {
      rawStock.push({
        amount: entry.quantity,
        date: entry.date,
        type: "production",
      });
    });
  }

  if (packageData.replenishmentLeadTimeDate) {
    rawStock.push({
      amount: 0,
      date: packageData.replenishmentLeadTimeDate,
      type: "replenishment",
    });
  }

  rawStock.sort((a, b) => {
    if (dayjs(a.date).startOf("day").isBefore(dayjs(b.date).startOf("day"))) {
      return -1;
    } else if (dayjs(a.date).startOf("day").isAfter(dayjs(b.date).startOf("day"))) {
      return 1;
    }

    return TYPE_ORDER[a.type] - TYPE_ORDER[b.type];
  });

  const byDate = groupByFn(rawStock, (entry) => dayjs(entry.date).startOf("day").toISOString());

  // Sum quantities for the same day
  const stock: StockData = Object.values(byDate).map((entries) => ({
    ...entries[0],
    amount: sum(entries.map((_) => _.amount)),
  }));

  return stock;
}

export function combineDeliveries(stockData: StockEntry[]): StockEntry | null {
  if (!stockData.length) {
    return null;
  }
  const lastEntry = stockData[stockData.length - 1];

  const totalQty = stockData.map((stock) => stock.amount).reduce((a, b) => a + b, 0);

  return {
    ...lastEntry,
    amount: totalQty,
  };
}
